import React, { useMemo } from 'react';
import { Grid, SpacingName, TypographyName } from '@doveit/lego';
import { NextPage } from 'next';
import { formatOpeningHours } from '@doveit/hammer';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import httpErrors from '../client/utils/error/http/httpErrors';
import {
  buildAgenciesURLObject, goToPage, buildSerpURLObject, buildAgencyByLocalityURLObject, buildRealEstateAgencyURLObject, relativeToAbsoluteURL,
  JOIN_OUR_AGENTS_AS,
  buildSellPropertyURLObject,
  buildAgentURLObject,
} from '../client/utils/links';
import {
  getStructuredData, getBreadcrumbStructuredData,
} from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import PageSection from '../client/components/page-section/PageSection';
import { HeroSection, TextAlign } from '../client/types';
import * as domain from '../domain/graphcms';
import { getLogger } from '../logger/isomorphic';
import { Status } from '../domain/status';
import { SortOrder } from '../domain/pagination';
import { getAll } from '../services/properties/properties';
import * as styles from '../client/templates/agency/Agency.style';
import AgencyCardList from '../client/components/agency-card-list/AgencyCardList';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import Hero from '../client/templates/common/hero/Hero';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { sortAgents } from '../services/agents/sortAgents';
import QuickAction from '../client/components/quick-action/QuickAction';
import { buildEvaluationByLocalityURLObject } from '../client/utils/links/evaluation';
import { AGENCY_BY_LOCALITY_SLUG } from '../client/graphql/queries/agency-by-locality';
import { template } from '../client/utils/string/string';
import { getRealEstateAgencyStructuredData } from '../data/structured/realEstateAgency';
import {
  BannerSection, BannerSectionProps, GenericSection, GenericSectionProps, NewsSection, NewsSectionProps, PropertiesForSaleSection, PropertiesForSaleSectionProps, ReviewsSection, ReviewsSectionProps, SoldPropertiesSection, SoldPropertiesSectionProps, TeamSection, TeamSectionProps,
} from '../client/sections';

export interface AgencyProps {
  SEO: PageHeadProps['SEO'],
  locality: string,
  provinceSlug: string,
  agency: Omit<domain.Agency, 'agents' | 'seo'>,
  hero: HeroSection,
  hasEvaluation: boolean,
  teamSection: TeamSectionProps,
  joinUsSection: BannerSectionProps | null,
  homeToSellSection: GenericSectionProps,
  propertiesForSaleSection: PropertiesForSaleSectionProps | null,
  soldPropertiesSection: SoldPropertiesSectionProps | null,
  newsSection: NewsSectionProps | null,
  reviewsSection: ReviewsSectionProps | null,
  realEstateAgencies: {
    title: string,
  },
  localityHasSellProperties: boolean,
}
const logger = getLogger();

export const Agency: NextPage<AgencyProps> = ({
  SEO,
  agency,
  newsSection,
  reviewsSection,
  hero,
  hasEvaluation,
  joinUsSection,
  homeToSellSection,
  teamSection,
  propertiesForSaleSection,
  soldPropertiesSection,
  locality,
  provinceSlug,
  realEstateAgencies,
  localityHasSellProperties,
}) => {
  const serpURLObject = buildSerpURLObject({ province: provinceSlug });
  const evaluationURLObject = buildEvaluationByLocalityURLObject({ locality });

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Agenzie immobiliari',
      urlObject: buildAgenciesURLObject(),
    },
    {
      label: locality,
      urlObject: buildAgencyByLocalityURLObject({ locality }),
    },
  ], [locality]);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={
          getStructuredData(
            getBreadcrumbStructuredData(breadcrumb),
            agency.realEstateAgencies.length > 0
              ? agency.realEstateAgencies.map((rea) => (
                rea.address
                  ? getRealEstateAgencyStructuredData(
                    rea.name,
                    rea.address.locality,
                    {
                      ...rea.address,
                      url: `${SEO.canonical}/${rea.slug}`,
                    },
                    rea.phoneNumber ?? '+39 327 2074613',
                    rea.openingHours,
                  )
                  : undefined
              )).filter((arg) => !!arg)
              : undefined,
          )
        }
      />

      <PageLayout>
        <Hero
          title={hero.title}
          backgroundImage={hero.backgroundImage}
          primarySlot={(
            <Grid
              gutter={SpacingName.SMALLER}
              css={styles.heroActions}
            >
              {hasEvaluation && (
                <Grid.Unit size={{ LG: 1 / 3 }}>
                  <QuickAction
                    aria-label="evaluation-hero-card"
                    action="Valutazione Gratuita Case"
                    locality={locality}
                    image={{
                      src: requireFromStaticAsset('symbols/noun-key.svg'),
                      alt: `Valutazione gratuita case a ${locality}`,
                    }}
                    urlObject={evaluationURLObject}
                  />
                </Grid.Unit>
              )}
              {localityHasSellProperties && (
                <Grid.Unit size={{ LG: 1 / 3 }}>
                  <QuickAction
                    aria-label="locality-has-sell-properties-hero-card"
                    action="Vendita Immobili"
                    locality={locality}
                    urlObject={buildSellPropertyURLObject(locality)}
                    image={
                      {
                        src: requireFromStaticAsset('symbols/group.svg'),
                        alt: `Vendita immobili a ${locality}`,
                      }
                    }
                  />
                </Grid.Unit>
              )}
              {propertiesForSaleSection && (
                <Grid.Unit size={{ LG: 1 / 3 }}>
                  <QuickAction
                    aria-label="properties-for-sale-hero-card"
                    action="Acquistare Casa"
                    locality={locality}
                    urlObject={serpURLObject}
                    image={
                      {
                        src: requireFromStaticAsset('symbols/level1.svg'),
                        alt: `Acquistare casa a ${locality}`,
                      }
                    }
                  />
                </Grid.Unit>
              )}
            </Grid>
          )}
        />
        <PageLayout.Content addCss={styles.content}>
          <PageLayout.Main>
            <PageSection data-ref="agency-description">
              <PageSection.Wrapper>
                <PageSection.Description text={agency.info} />
              </PageSection.Wrapper>
            </PageSection>

            {propertiesForSaleSection && (
              <PropertiesForSaleSection {...propertiesForSaleSection} />
            )}

            {agency.realEstateAgencies.length > 0 && (
              <PageSection data-ref="real-estate-agencies">
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={realEstateAgencies.title}
                  />
                  <PageSection.Block>
                    <AgencyCardList
                      items={
                        agency.realEstateAgencies
                          .map((realEstateAgency) => {
                            const realEstateUrl = buildRealEstateAgencyURLObject({
                              locality: realEstateAgency.parent?.locality.slug,
                              slug: realEstateAgency.slug,
                            });

                            return {
                              id: realEstateAgency.id,
                              name: realEstateAgency.name,
                              address: `${realEstateAgency.address?.streetAddress} - ${realEstateAgency.address?.locality}`,
                              urlObject: realEstateUrl,
                              franchising: realEstateAgency.franchising,
                              picture: realEstateAgency.imageBlock?.image?.url,
                              openingHours: realEstateAgency.openingHours ? formatOpeningHours(realEstateAgency.openingHours) : undefined,
                              agents: realEstateAgency.agents.map((broker) => ({
                                name: `${broker.name} ${broker.surname}`,
                                jobTitle: broker.jobTitleType.name,
                                franchising: true,
                                picture: broker.photo,
                                locality,
                              })),
                              onClick: () => goToPage(realEstateUrl),
                            };
                          })
                      }
                    />
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            {teamSection && (
              <TeamSection {...teamSection} />
            )}

            {joinUsSection && (
              <BannerSection {...joinUsSection} />
            )}

            {soldPropertiesSection && (
              <SoldPropertiesSection {...soldPropertiesSection} />
            )}

            {reviewsSection && (
              <ReviewsSection {...reviewsSection} />
            )}

            {newsSection && (
              <NewsSection {...newsSection} />
            )}

            <GenericSection
              {...homeToSellSection}
              backgroundColor="neutrals.lighter"
              titleColor="brand.primary"
            />

            <PageSection data-ref="lead-form-section">
              <PageSection.Wrapper>
                <PageSection.Title
                  content={agency.contactSectionTitle}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Description
                  text={agency.contactSectionSubtitle}
                  textAlign={TextAlign.CENTER}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <CreateLeadSection
                  title={agency.contactSectionLabel}
                  dataRef="lead-form-main"
                  onSuccess={(lead) => sendLeadSubmitEventAndRedirect('main', lead)}
                  dataTracking="lead-form-main-submit"
                />
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
        <PageLayout.Wrapper>
          <PageLayout.Breadcrumb items={breadcrumb} />
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<AgencyProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const { locality: slug } = query;

  const { data: dataAgency } = await graphQLClient.query<{
    directAgents: domain.Agent[],
    franchisingAgents: domain.Agent[],
    locality: domain.Locality,
    agency: domain.Agency,
    joinUs: domain.JoinUsSectionComponent,
    news: domain.Article[],
    reviews: domain.Review[],
    sellPropertiesCount: {
      aggregate: { count: number },
    },
  }>({
    query: AGENCY_BY_LOCALITY_SLUG,
    variables: {
      slug,
      news: 4,
    },
  });

  assertNotNullOrUndefined(dataAgency.locality, new httpErrors.NotFound(`Cannot find locality with slug: ${slug}`));
  assertNotNullOrUndefined(dataAgency.locality.agency, new httpErrors.NotFound(`Cannot find agency for locality with slug: ${slug}`));

  const templatedDataAgency = template(dataAgency, { locality: dataAgency.locality.name });

  const {
    name, agency, province, evaluation,
  } = templatedDataAgency.locality;

  const {
    directAgents, franchisingAgents, news, reviews,
  } = templatedDataAgency;
  const allAgents = [
    ...sortAgents(directAgents).map((directAgent) => ({
      ...directAgent,
      franchising: false,
    })),
    ...sortAgents(franchisingAgents).map((franchisingAgent) => ({
      ...franchisingAgent,
      franchising: true,
    })),
  ];

  const { slug: provinceSlug } = province;
  const serpURLObject = buildSerpURLObject({ province: provinceSlug });

  const [liveProperties, soldPropertiesList] = await Promise.all([
    getAll(
      {
        province: provinceSlug,
        status: Status.LIVE,
      },
      {
        size: 4,
        sort: { published_at: SortOrder.DESC },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving live properties by province');
      return {
        content: [],
      };
    }),
    getAll(
      {
        province: provinceSlug,
        status: Status.VENDUTO,
      },
      {
        size: 4,
        sort: { sold_at: SortOrder.DESC },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving sold properties by province');
      return {
        content: [],
      };
    }),
  ]);

  const agencyByLocalityURLObject = buildAgencyByLocalityURLObject({ locality: name });

  return {
    props: {
      SEO: {
        ...agency.seo,
        canonical: relativeToAbsoluteURL(agencyByLocalityURLObject.as),
      },
      agency: {
        ...agency,
        info: agency.intro?.content?.markdown?.trim() || null,
        realEstateAgencies: agency.realEstateAgencies.map((realEstateAgencyItem) => ({
          ...realEstateAgencyItem,
          agents: realEstateAgencyItem.franchising
            ? sortAgents(realEstateAgencyItem.agents, false) : [],
        })),
        contactSectionTitle: agency.contact?.title?.markdown?.trim() || null,
        contactSectionSubtitle: agency.contact?.subtitle?.markdown?.trim() || null,
        contactSectionLabel: agency.contact?.formTitle?.trim() || null,
      },
      teamSection: agency.team
        ? {
          title: agency.team?.title?.markdown?.trim() || null,
          content: agency.team?.content?.markdown?.trim() || null,
          agents: allAgents.map((agent) => ({
            id: agent.id,
            name: `${agent.name} ${agent.surname}`,
            jobTitle: agent.jobTitleType.name,
            picture: agent.photo,
            urlObject: agent.biography && buildAgentURLObject({ slug: agent.slug }),
            franchising: agent.franchising,
            locality: name,
          })),
        } : null,
      joinUsSection: agency.joinUs ? {
        title: agency.joinUs.title.markdown,
        content: agency.joinUs.content.markdown,
        image: {
          url: agency.joinUs.image.url,
          alt: agency.joinUs.imageAlt,
        },
        action: {
          label: agency.joinUs.ctaLabel,
          href: JOIN_OUR_AGENTS_AS,
        },
      } : null,
      homeToSellSection: agency.preForm ? {
        title: agency.preForm.title?.markdown?.trim() || null,
        content: agency.preForm.content.markdown.trim(),
      } : null,
      hasEvaluation: !!evaluation?.id,
      localityHasSellProperties: templatedDataAgency.sellPropertiesCount.aggregate.count > 0,
      propertiesForSaleSection: liveProperties.content.length > 0
        ? {
          title: agency.latestProperties?.title?.markdown?.trim() || null,
          subtitle: agency.latestProperties?.subtitle?.markdown?.trim() || null,
          content: agency.latestProperties?.content?.markdown?.trim() || null,
          properties: liveProperties.content,
          action: {
            label: agency.latestProperties.ctaLabel,
            href: serpURLObject.as,
          },
        } : null,
      soldPropertiesSection: soldPropertiesList.content.length > 0 ? {
        title: agency.soldProperties.title?.markdown || null,
        subtitle: agency.soldProperties.subtitle?.markdown || null,
        content: agency.soldProperties.content?.markdown || null,
        properties: soldPropertiesList.content.map((property) => ({
          id: property.id,
          title: `${property.locality}${(property.suburb ? ` - ${property.suburb}` : '')}`,
          savingLabel: property.savingLabel || null,
          image: {
            url: property.coverImageSet.small,
            alt: property.title,
          },
        })),
      } : null,
      newsSection: news?.length > 1
        ? {
          title: agency.news.title.markdown,
          content: agency.news.content?.markdown || null,
          news: news.map((article) => ({
            id: article.id,
            title: article.title,
            alternateTitle: article.alternateTitle,
            abstract: article.abstract,
            image: {
              url: article.imageBlock?.image?.url,
              alt: article.title,
            },
          })),
        }
        : null,
      reviewsSection: reviews.length > 0
        ? {
          title: agency.review?.title?.markdown?.trim() || null,
          reviews,
          trustPilot: {
            label: 'Lascia una recensione',
            href: 'https://it.trustpilot.com/review/dove.it',
            dataRef: 'cta-leave-review',
            dataTracking: 'cta-leave-review',
          },
        }
        : null,
      locality: name,
      hero: {
        title: agency.hero?.title?.markdown?.trim() || null,
        description: agency.hero?.content?.markdown?.trim() || null,
        backgroundImage: {
          SMALL: agency.hero?.mobileImage?.url || null,
          LARGE: agency.hero?.desktopImage?.url || null,
        },
      },
      provinceSlug,
      realEstateAgencies: {
        title: agency.realEstateAgency?.title?.markdown?.trim() || null,
      },
    },
  };
});

export default withError(Agency);
